"use client";

import { useMemo, useState } from "react";

import { fmtIntCA } from "@/lib/format";

import {
  type BlocsView,
  type MatrixView,
  type Pair,
  blocAssignments,
  displayOrder,
  pairKey,
  pairMap,
} from "@/app/council/_lib/alignment-data";

import { VIRIDIS_GRADIENT, blocColor, buildAgreementScale } from "./colors";

type Props = {
  matrix: MatrixView;
  blocs: BlocsView | null;
  /** k cut used to colour the nodes (council scope only). */
  k?: 2 | 3 | 4;
};

// viewBox is sized to give the label tails ~100px of margin on either
// side of the ring so glyphs at the 3-o'clock / 9-o'clock positions
// don't get clipped when the SVG scales down on mobile (the right-
// anchored labels at angle≈0 used to drop their last character at
// 375px wide).
const W = 660;
const H = 560;
const CX = W / 2;
const CY = H / 2;
const RING_R = 196;
const LABEL_R = 226;
const NODE_R = 9;

const THRESHOLDS = [10, 25, 50, 100] as const;
type Threshold = (typeof THRESHOLDS)[number];

type Edge = {
  aIdx: number;
  bIdx: number;
  rate: number;
};

type NodeLayout = {
  id: number;
  name: string;
  shortLabel: string;
  angle: number;
  x: number;
  y: number;
  labelX: number;
  labelY: number;
  labelAnchor: "start" | "end" | "middle";
  bloc: string | undefined;
};

/**
 * Circular "chord" view of the pairwise agreement matrix. Each
 * councillor sits at a fixed angle around a ring (ordered by the bloc
 * clustering when blocs.json is available so adjacent dots tend to be
 * politically adjacent), and edges connect pairs whose agreement rate
 * lands in the chosen "top N%" slice.
 *
 * The reader controls the density via the threshold toggles
 * (10/25/50/100). At low thresholds the figure looks like local
 * triangles inside each bloc — the tightest pockets of agreement.
 * Raising the threshold pulls in the cross-bloc connections.
 *
 * Hovering or focusing a node spotlights every edge incident on that
 * councillor and surfaces their top-aligned and least-aligned peers in
 * the side panel — a per-councillor read that the heatmap can't give
 * without scanning a row.
 */
export function AlignmentChord({ matrix, blocs, k = 3 }: Props) {
  const order = useMemo(() => displayOrder(matrix, blocs), [matrix, blocs]);
  const pairs = useMemo(() => pairMap(matrix), [matrix]);
  const blocs_ = useMemo(() => blocAssignments(blocs, k), [blocs, k]);
  const scale = useMemo(() => buildAgreementScale(matrix.pairs), [matrix.pairs]);

  const [threshold, setThreshold] = useState<Threshold>(25);
  const [hoverId, setHoverId] = useState<number | null>(null);

  const N = order.length;

  const nodes = useMemo<NodeLayout[]>(() => {
    return order.map((voter, i) => {
      const angle = (i / N) * Math.PI * 2 - Math.PI / 2;
      const x = CX + Math.cos(angle) * RING_R;
      const y = CY + Math.sin(angle) * RING_R;
      const labelX = CX + Math.cos(angle) * LABEL_R;
      const labelY = CY + Math.sin(angle) * LABEL_R;
      // Anchor labels to the side they fall on so they don't run into the ring.
      const cos = Math.cos(angle);
      const labelAnchor: "start" | "end" | "middle" =
        Math.abs(cos) < 0.15 ? "middle" : cos > 0 ? "start" : "end";
      return {
        id: voter.id,
        name: voter.display_name,
        shortLabel: shortenLabel(voter.display_name),
        angle,
        x,
        y,
        labelX,
        labelY,
        labelAnchor,
        bloc: blocs_.get(voter.id),
      };
    });
  }, [order, N, blocs_]);

  const allEdges = useMemo<Edge[]>(() => {
    const out: Edge[] = [];
    for (let i = 0; i < N; i++) {
      for (let j = i + 1; j < N; j++) {
        const p = pairs.get(pairKey(order[i].id, order[j].id));
        if (!p || p.agreement_rate === null) continue;
        out.push({ aIdx: i, bIdx: j, rate: p.agreement_rate });
      }
    }
    out.sort((a, b) => b.rate - a.rate);
    return out;
  }, [pairs, order, N]);

  const visibleEdges = useMemo(() => {
    const count = Math.max(1, Math.round((allEdges.length * threshold) / 100));
    return allEdges.slice(0, count);
  }, [allEdges, threshold]);

  // Peers list for the hovered node — used by the side panel.
  const peerStats = useMemo(() => {
    if (hoverId === null) return null;
    const peers: { peer: NodeLayout; pair: Pair }[] = [];
    for (const node of nodes) {
      if (node.id === hoverId) continue;
      const p = pairs.get(pairKey(hoverId, node.id));
      if (!p || p.agreement_rate === null) continue;
      peers.push({ peer: node, pair: p });
    }
    peers.sort((a, b) => (b.pair.agreement_rate ?? 0) - (a.pair.agreement_rate ?? 0));
    return {
      voter: nodes.find((n) => n.id === hoverId) ?? null,
      top: peers.slice(0, 3),
      bottom: peers.slice(-3).reverse(),
    };
  }, [hoverId, nodes, pairs]);

  // Index nodes that are connected to the hovered node via the
  // currently-visible edge set. Used to mute the rest.
  const spotlightedIds = useMemo(() => {
    if (hoverId === null) return null;
    const idx = nodes.findIndex((n) => n.id === hoverId);
    if (idx === -1) return null;
    const set = new Set<number>([hoverId]);
    for (const e of visibleEdges) {
      if (e.aIdx === idx) set.add(nodes[e.bIdx].id);
      else if (e.bIdx === idx) set.add(nodes[e.aIdx].id);
    }
    return set;
  }, [hoverId, nodes, visibleEdges]);

  return (
    <div>
      <div className="mb-3 flex items-baseline justify-between flex-wrap gap-3 cs-mono text-xs">
        <span className="text-ink-3">
          {fmtIntCA(visibleEdges.length)} of {fmtIntCA(allEdges.length)} pairs
          shown · ordered by bloc
        </span>
        <fieldset
          aria-label="Edge density"
          className="flex items-center gap-1"
        >
          <span className="text-ink-3 mr-1">show</span>
          {THRESHOLDS.map((t) => {
            const active = t === threshold;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setThreshold(t)}
                aria-pressed={active}
                className={
                  "px-2 py-1 border " +
                  (active
                    ? "border-ink bg-ink text-bg"
                    : "border-rule text-ink-2 hover:border-ink-3")
                }
              >
                {t === 100 ? "all pairs" : `top ${t}%`}
              </button>
            );
          })}
        </fieldset>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="overflow-auto -mx-2 px-2 flex-1">
          <svg
            role="img"
            aria-label={`Circular vote-alignment view for ${matrix.scope.label}`}
            width={W}
            height={H}
            viewBox={`0 0 ${W} ${H}`}
            style={{ maxWidth: "100%", height: "auto" }}
            onMouseLeave={() => setHoverId(null)}
          >
            {visibleEdges.map((e, i) => {
              const a = nodes[e.aIdx];
              const b = nodes[e.bIdx];
              const isSpot =
                hoverId !== null &&
                (a.id === hoverId || b.id === hoverId);
              const muted = hoverId !== null && !isSpot;
              return (
                <line
                  key={`e-${i}`}
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  stroke={scale.toColor(e.rate)}
                  strokeWidth={isSpot ? 2 : 1}
                  opacity={muted ? 0.06 : 0.55}
                  style={{ pointerEvents: "none" }}
                />
              );
            })}

            <text
              x={W - 10}
              y={H - 8}
              textAnchor="end"
              aria-hidden
              style={{
                font: "10px var(--font-mono, ui-monospace), monospace",
                fill: "var(--ink-3)",
                letterSpacing: "0.04em",
                pointerEvents: "none",
              }}
            >
              calgarylens.ca
            </text>

            {nodes.map((node) => {
              const muted =
                spotlightedIds !== null && !spotlightedIds.has(node.id);
              const isHover = hoverId === node.id;
              const fill = node.bloc
                ? blocColor(node.bloc)
                : "var(--ink-2)";
              return (
                <g
                  key={`n-${node.id}`}
                  onMouseEnter={() => setHoverId(node.id)}
                  onFocus={() => setHoverId(node.id)}
                  onBlur={() => setHoverId((cur) => (cur === node.id ? null : cur))}
                  tabIndex={0}
                  style={{ cursor: "pointer", outline: "none" }}
                >
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={isHover ? NODE_R + 2 : NODE_R}
                    fill={fill}
                    stroke="var(--ink)"
                    strokeWidth={isHover ? 2 : 0.5}
                    opacity={muted ? 0.35 : 1}
                  />
                  <text
                    x={node.labelX}
                    y={node.labelY}
                    textAnchor={node.labelAnchor}
                    dominantBaseline="middle"
                    style={{
                      font: "13px var(--font-mono, ui-monospace), monospace",
                      fill: muted ? "var(--ink-3)" : "var(--ink)",
                      fontWeight: isHover ? 700 : 500,
                      pointerEvents: "none",
                    }}
                  >
                    {node.shortLabel}
                  </text>
                  <title>{node.name}</title>
                </g>
              );
            })}
          </svg>
        </div>

        <aside
          aria-live="polite"
          className="cs-paper-light px-4 py-3 border border-rule lg:w-[300px] lg:flex-shrink-0"
        >
          <div className="cs-label">councillor</div>
          {peerStats?.voter ? (
            <>
              <div className="mt-1 font-medium leading-snug text-ink">
                {peerStats.voter.name}
              </div>
              <div className="mt-3 cs-rule pt-2">
                <div className="cs-label text-moss">
                  most aligned with
                </div>
                <ul className="mt-1 flex flex-col gap-0.5 text-sm">
                  {peerStats.top.map(({ peer, pair }) => (
                    <li key={`top-${peer.id}`} className="flex justify-between gap-2">
                      <span className="text-ink-2">{peer.name}</span>
                      <span className="cs-mono text-ink-3 text-xs">
                        {Math.round((pair.agreement_rate ?? 0) * 100)}%
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-3 cs-rule pt-2">
                <div className="cs-label text-brick">
                  least aligned with
                </div>
                <ul className="mt-1 flex flex-col gap-0.5 text-sm">
                  {peerStats.bottom.map(({ peer, pair }) => (
                    <li key={`bot-${peer.id}`} className="flex justify-between gap-2">
                      <span className="text-ink-2">{peer.name}</span>
                      <span className="cs-mono text-ink-3 text-xs">
                        {Math.round((pair.agreement_rate ?? 0) * 100)}%
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <p className="mt-1 text-sm text-ink-2 leading-snug">
              Hover over any dot (or press Tab on a keyboard) to spotlight
              that councillor&rsquo;s ties and see their closest and
              most-distant peers.
            </p>
          )}

          {!scale.degenerate && (
            <div className="mt-3 cs-rule pt-2 cs-mono text-[12px] text-ink-3 leading-relaxed">
              <div className="mb-1 text-ink-2">edge colour</div>
              <div
                aria-hidden
                style={{
                  background: VIRIDIS_GRADIENT,
                  height: 8,
                  borderRadius: 1,
                }}
              />
              <div className="mt-1 flex items-center justify-between cs-num">
                <span>{Math.round(scale.min * 100)}%</span>
                <span className="text-ink-3">
                  {Math.round(scale.median * 100)}%
                </span>
                <span>{Math.round(scale.max * 100)}%</span>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

/**
 * Compact label for the ring. Drops both the "Mayor " prefix and the
 * "(W#)" ward suffix so the labels fit at mobile widths without
 * crowding into each other — the bloc-colour dot beside each name
 * already groups members by ward neighbourhood, and the full
 * "(W#)"-suffixed name appears in the side panel on hover. Keeps
 * the literal display name otherwise.
 */
function shortenLabel(displayName: string): string {
  return displayName
    .replace(/^Mayor\s+/, "")
    .replace(/\s*\(W\d+\)\s*$/, "")
    .trim();
}
